import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "locales";
import providers from "lib/providers";
import "./style.scss";

interface Props {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

interface CustomEmojiItem {
  id: string;
  name: string;
  fileUuid: string;
  fileUrl: string;
  enabled: boolean;
}

interface EmojiCategory {
  icon: string;
  label: string;
  emojis: string[];
}

const CATEGORIES: EmojiCategory[] = [
  {
    icon: "😀",
    label: "Smileys",
    emojis: [
      "😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃",
      "😉","😊","😇","🥰","😍","🤩","😘","😗","😚","😙",
      "🥲","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🫢",
      "🤫","🤔","🫡","🤐","🤨","😐","😑","😶","🫥","😏",
      "😒","🙄","😬","🤥","😌","😔","😪","🤤","😴","😷",
      "🤒","🤕","🤢","🤮","🥵","🥶","🥴","😵","🤯","🤠",
      "🥳","🥸","😎","🤓","🧐","😕","🫤","😟","🙁","😮",
      "😯","😲","😳","🥺","🥹","😦","😧","😨","😰","😥",
      "😢","😭","😱","😖","😣","😞","😓","😩","😫","🥱",
      "😤","😡","😠","🤬","😈","👿","💀","☠️","💩","🤡",
    ],
  },
  {
    icon: "👋",
    label: "Gestures",
    emojis: [
      "👋","🤚","🖐️","✋","🖖","🫱","🫲","🫳","🫴","👌",
      "🤌","🤏","✌️","🤞","🫰","🤟","🤘","🤙","👈","👉",
      "👆","🖕","👇","☝️","🫵","👍","👎","✊","👊","🤛",
      "🤜","👏","🙌","🫶","👐","🤲","🤝","🙏","✍️","💅",
      "🤳","💪","🦾","🦿","🦵","🦶","👂","🦻","👃","🧠",
    ],
  },
  {
    icon: "❤️",
    label: "Hearts",
    emojis: [
      "❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔",
      "❤️‍🔥","❤️‍🩹","❣️","💕","💞","💓","💗","💖","💘","💝",
      "💟","☮️","✝️","☪️","🕉️","☸️","✡️","🔯","🕎","☯️",
    ],
  },
  {
    icon: "🐶",
    label: "Animals",
    emojis: [
      "🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐻‍❄️","🐨",
      "🐯","🦁","🐮","🐷","🐸","🐵","🙈","🙉","🙊","🐒",
      "🐔","🐧","🐦","🐤","🐣","🐥","🦆","🦅","🦉","🦇",
      "🐺","🐗","🐴","🦄","🐝","🪱","🐛","🦋","🐌","🐞",
    ],
  },
  {
    icon: "🍕",
    label: "Food",
    emojis: [
      "🍏","🍎","🍐","🍊","🍋","🍌","🍉","🍇","🍓","🫐",
      "🍈","🍒","🍑","🥭","🍍","🥥","🥝","🍅","🍆","🥑",
      "🥦","🥬","🌶️","🫑","🌽","🥕","🫒","🧄","🧅","🥔",
      "🍕","🍔","🍟","🌭","🍿","🧂","🥚","🍳","🧇","🥞",
    ],
  },
  {
    icon: "⚽",
    label: "Activities",
    emojis: [
      "⚽","🏀","🏈","⚾","🥎","🎾","🏐","🏉","🥏","🎱",
      "🪀","🏓","🏸","🏒","🏑","🥍","🏏","🪃","🥅","⛳",
      "🪁","🏹","🎣","🤿","🥊","🥋","🎽","🛹","🛼","🛷",
      "⛸️","🥌","🎿","⛷️","🏂","🪂","🏋️","🤸","🤺","⛹️",
    ],
  },
  {
    icon: "🚗",
    label: "Travel",
    emojis: [
      "🚗","🚕","🚙","🚌","🚎","🏎️","🚓","🚑","🚒","🚐",
      "🛻","🚚","🚛","🚜","🏍️","🛵","🚲","🛴","🛺","🚔",
      "✈️","🛩️","🛫","🛬","🪂","💺","🚀","🛸","🚁","⛵",
      "🚤","🛥️","🛳️","⛴️","🚢","🏠","🏡","🏢","🏣","🏤",
    ],
  },
  {
    icon: "💡",
    label: "Objects",
    emojis: [
      "⌚","📱","📲","💻","⌨️","🖥️","🖨️","🖱️","🖲️","💾",
      "💿","📀","📷","📸","📹","🎥","📽️","🎞️","📞","☎️",
      "📺","📻","🎙️","🎚️","🎛️","⏱️","⏲️","⏰","🕰️","💡",
      "🔦","🕯️","📔","📕","📖","📗","📘","📙","📚","📓",
    ],
  },
];

const CUSTOM_TAB_INDEX = CATEGORIES.length;

function EmojiPicker({ onSelect, onClose }: Props) {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);
  const [customEmojis, setCustomEmojis] = useState<CustomEmojiItem[]>([]);
  const [customLoaded, setCustomLoaded] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  useEffect(() => {
    if ((activeCategory === CUSTOM_TAB_INDEX || search.trim()) && !customLoaded) {
      providers.CustomEmojisProvider.getAll()
        .then((data: CustomEmojiItem[]) => { setCustomEmojis(data); setCustomLoaded(true); })
        .catch(() => setCustomLoaded(true));
    }
  }, [activeCategory, customLoaded, search]);

  const filteredEmojis = useMemo(() => {
    if (!search.trim()) return activeCategory < CATEGORIES.length ? CATEGORIES[activeCategory].emojis : [];
    const q = search.toLowerCase();
    const all: string[] = [];
    CATEGORIES.forEach((cat) => {
      cat.emojis.forEach((e) => {
        if (e.includes(q)) all.push(e);
      });
    });
    return all;
  }, [search, activeCategory]);

  const handleSelect = useCallback(
    (emoji: string) => {
      onSelect(emoji);
    },
    [onSelect]
  );

  return (
    <div className="emoji-picker" ref={pickerRef}>
      <div className="emoji-picker__header">
        {CATEGORIES.map((cat, i) => (
          <button
            key={cat.label}
            type="button"
            className={"emoji-picker__category-btn" + (i === activeCategory && !search ? " __active" : "")}
            onClick={() => {
              setActiveCategory(i);
              setSearch("");
            }}
            title={cat.label}
          >
            {cat.icon}
          </button>
        ))}
        <button
          type="button"
          className={"emoji-picker__category-btn" + (activeCategory === CUSTOM_TAB_INDEX && !search ? " __active" : "")}
          onClick={() => { setActiveCategory(CUSTOM_TAB_INDEX); setSearch(""); }}
          title={t("Custom")}
        >
          ⭐
        </button>
      </div>
      <div className="emoji-picker__search">
        <input
          type="text"
          placeholder={t("Search emoji...")}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        />
      </div>
      <div className="emoji-picker__grid">
        {search.trim() && customEmojis.length > 0 && (
          customEmojis
            .filter((ce) => ce.name.toLowerCase().includes(search.toLowerCase()))
            .map((ce) => (
              <button
                key={`custom-${ce.id}`}
                type="button"
                className="emoji-picker__emoji-btn emoji-picker__custom-emoji"
                onClick={() => handleSelect(`:${ce.name}:`)}
                title={`:${ce.name}:`}
              >
                <img src={ce.fileUrl || `/files/${ce.fileUuid}`} alt={ce.name} width={24} height={24} />
              </button>
            ))
        )}
        {activeCategory === CUSTOM_TAB_INDEX && !search ? (
          customEmojis.length > 0 ? (
            customEmojis.map((ce) => (
              <button
                key={ce.id}
                type="button"
                className="emoji-picker__emoji-btn emoji-picker__custom-emoji"
                onClick={() => handleSelect(`:${ce.name}:`)}
                title={`:${ce.name}:`}
              >
                <img src={ce.fileUrl || `/files/${ce.fileUuid}`} alt={ce.name} width={24} height={24} />
              </button>
            ))
          ) : (
            <div className="emoji-picker__empty">{customLoaded ? t("No custom emoji") : t("Loading...")}</div>
          )
        ) : filteredEmojis.length > 0 ? (
          filteredEmojis.map((emoji, i) => (
            <button
              key={`${emoji}-${i}`}
              type="button"
              className="emoji-picker__emoji-btn"
              onClick={() => handleSelect(emoji)}
            >
              {emoji}
            </button>
          ))
        ) : (
          <div className="emoji-picker__empty">{t("No emoji found")}</div>
        )}
      </div>
    </div>
  );
}

export default EmojiPicker;
